import { Auth0ContextInterface } from '@auth0/auth0-react';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import { Banner, Button, Flex, Spacer } from '@electricjs/arc';

import { clearSelectedOrgFromStorage } from '@/pages/OrgSwitcher/utils';
import { useAppDispatch } from '@/redux/store';
import EmailNotVerifiedErrorPage from '@/components/Errors/EmailNotVerifiedErrorPage';

type Auth0ErrorStateProps = {
  auth0Error: Error;
  logout: Auth0ContextInterface['logout'];
  loggedOut: ActionCreatorWithPayload<void, string>;
};

export const Auth0ErrorState = ({
  auth0Error,
  logout,
  loggedOut,
}: Auth0ErrorStateProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');

  const errorMap: Record<string, string> = {
    login_failure: t('errors.auth0Error.loginFailure'),
    user_not_found: t('errors.auth0Error.userNotFound'),
    user_has_no_organizations: t('errors.auth0Error.userHasNoOrganizations'),
  };

  const message = errorMap[auth0Error.message]
    ? errorMap[auth0Error.message]
    : errorMap.login_failure;

  const handleLogOut = () => {
    // log out from auth0
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

    dispatch(loggedOut()); // clear states on redux
    clearSelectedOrgFromStorage();
  };

  if (auth0Error.message === 'email_not_verified') {
    return <EmailNotVerifiedErrorPage handleLogOut={handleLogOut} />;
  }

  return (
    <Flex
      width="100%"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      mt="10rem">
      <Banner
        id="login-error-banner"
        isFlat
        intent="error"
        title={t('errors.auth0Error.loginError')}
        message={message}
      />
      <Spacer space={6} />
      <Button
        id="button-log-out-error-page"
        variant="fill"
        intent="danger"
        onClick={handleLogOut}>
        {t('errors.auth0Error.logOut')}
      </Button>
    </Flex>
  );
};
